import { sortBy, uniq, flatMap, filter } from 'lodash-es';
import Model from '@/common/models/common.model';
import { store } from '@/common/root';
import { ACCESS_DIRECT, COMPUTE_NETWORK_TYPES, TYPES, ACCESS_ROUTED } from '@/common/constants/networks.constants';
import ipaddr from 'ipaddr.js';
import { getMatch } from 'ip-matching';
import Subnet from '@/common/models/subnet.model';
import {
  Network as NetworkSchema,
  DomainProject as DomainProjectSchema
} from '@schema';

export default class Network extends Model {
  declare attributes: NetworkSchema;

  get project(): DomainProjectSchema {
    return (this.projectId && typeof store.getters['projects/getItemById'] === 'function'
      && store.getters['projects/getItemById'](this.projectId)) || {};
  }

  get uuid(): string {
    return this.id;
  }

  get subnet() {
    return this.attributes.subnet;
  }

  get subnets(): Subnet[] {
    const { subnets: originalSubnets = [this.subnet] } = this.attributes;
    // it is always better to work with a separated store data
    const subnetsInStore = (typeof store.getters['subnets/getItemsByView'] === 'function')
      ? store.getters['subnets/getItemsByView'](this.id)
      : [];

    const subnets = (!subnetsInStore?.length && originalSubnets.length)
      ? originalSubnets.map(subnet => new Subnet(subnet))
      : subnetsInStore;

    // display ipv4 subnet first
    return sortBy(subnets, ['ipVersion']);
  }

  get type() {
    return TYPES[this.attributes.type] || this.attributes.type;
  }

  get originType() {
    return this.attributes.type;
  }

  get cidr() {
    return this.subnets.map(sub => sub.cidr);
  }

  get gateway() {
    return this.subnets.reduce((acc, sub) => sub.gatewayIp ? acc.concat(sub.gatewayIp) : acc, [] as string[]);
  }

  get shared() {
    return this.attributes.shared;
  }

  get dhcpEnabled() {
    return !!this.subnets.find(sub => sub.enableDhcp);
  }

  get ipManagement() {
    return this.attributes.ipam_enabled;
  }

  get routerExternal() {
    return this.attributes.router_external;
  }

  get allocationPools() {
    return this.subnets.reduce((acc, sub) => sub.allocationPools?.length
        ? acc.concat(sub.allocationPools)
        : acc,
      [] as AllocationPool[]);
  }

  get dns() {
    return this.subnets.reduce((acc, sub) => sub.dnsNameservers?.length
        ? acc.concat(sub.dnsNameservers)
        : acc,
      [] as string[]);
  }

  get physicalNetwork() {
    return this.attributes.physical_network;
  }

  get projectId() {
    return this.attributes.project_id;
  }

  get domainId() {
    return this.attributes.domain_id;
  }

  get projectName() {
    return this.project.name;
  }

  get isUnmanaged() {
    return !this.attributes.ipam_enabled;
  }

  get vlanId() {
    return this.attributes.vlan_id;
  }

  get mtu() {
    return this.attributes.mtu;
  }

  get rbacPolicies() {
    return this.attributes.rbac_policies;
  }

  get spoofingProtection(): boolean {
    return this.attributes.spoofing_protection;
  }

  get spoofingProtectionAllowed() {
    return this.spoofingProtection && this.ipManagement;
  }

  get securityGroupsAllowed() {
    return this.spoofingProtection;
  }

  get hasIpv6() {
    return !!this.subnets.find(it => it.ipVersion === 6);
  }

  get hasIpv4() {
    return !!this.subnets.find(it => it.ipVersion === 4);
  }

  get routedAccessOnly() {
    return this.type === COMPUTE_NETWORK_TYPES.EXTERNAL
      && this.ipManagement
      && this.subnets.length === 0;
  }

  accessByProjectId(projectId: ID) {
    return uniq(
      flatMap(
        filter(
          this.rbacPolicies,
          item => item.target_project === projectId || item.target_project === '*'
        ),
        'actions'
      )
    );
  }

  hasDirectAccess(projectId) {
    return this.accessByProjectId(projectId).includes(ACCESS_DIRECT);
  }

  hasRoutedAccess(projectId) {
    return this.accessByProjectId(projectId).includes(ACCESS_ROUTED);
  }

  isAvailableForProject(projectId: ID): boolean {
    return this.projectId === projectId || this.hasDirectAccess(projectId);
  }

  isInCidr(ipaddress): boolean {
    if (!this.cidr || !ipaddress) {
      return false;
    }

    const addr = ipaddr.parse(ipaddress);

    return !!this.subnets
      .filter(subnet => addr.kind() === subnet.kind)
      .find(subnet => subnet.cidr && addr.match(ipaddr.parseCIDR(subnet.cidr)));
  }

  // TODO move method ipIsInPool to the commons
  isInPool(ipaddress): boolean {
    return !!this.allocationPools.find(
      ({ startAddress, endAddress }) => {
        const currentRange = getMatch(`${startAddress}-${endAddress}`);

        return currentRange.matches(ipaddress);
      }
    );
  }
}
